import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReactComponent as TPillars } from "../assets/mainlogo.svg";
import { ReactComponent as DiaryIcon } from "../assets/diary.svg";
import { ReactComponent as ModIcon } from "../assets/mod.svg";
import { ReactComponent as MealsIcon } from "../assets/meals.svg";
import { ReactComponent as ProfileIcon } from "../assets/profile.svg";
import { ReactComponent as ProgramIcon } from "../assets/programs.svg";
import { ReactComponent as ActiveDiaryIcon } from "../assets/active-diary.svg";
import { ReactComponent as ActiveModIcon } from "../assets/active-mod.svg";
import { ReactComponent as ActiveMealsIcon } from "../assets/active-meals.svg";
import { ReactComponent as ActiveProfileIcon } from "../assets/active-profile.svg";
import { ReactComponent as ActiveProgramIcon } from "../assets/active-programs.svg";
import { ReactComponent as PremiumIcon } from "../assets/premium.svg";
import {
  Button,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  Badge
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import SubscriptionModal from "../components/Modals/SubscriptionModal";
import { useCurrentUser } from "../components/Extra/UserContext";
import FreeTrialModal from "../components/Modals/FreeTrialModal";
import dayjs from "dayjs";
import useIsMobile from "../components/Extra/useMobile";
import CouponModal from "../components/Modals/CouponModal";
import { useSelector, useDispatch } from "react-redux";
import { selectPendingReadNotification } from "../features/auth/authSelectors";
import { LiveHelp, ChatBubbleOutline, Chat } from "@mui/icons-material";
import {
  fetchUserConversation,
  appendMessage,
  updateMessageStatus
} from "../features/messaging/messagingSlice";
import useFaviconBadge from "../utils/useFaviconBadge";
import { io } from "socket.io-client";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

const drawerWidth = 265;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 10px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 24px)`
  }
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  height: "92px",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(4, 1),
  ...theme.mixins.toolbar,
  position: "relative" // Add relative position for absolute positioning of the toggle button
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open"
})(({ theme, open }) => ({
  width: drawerWidth - 20,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  overflow: "visible",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      overflow: "visible"
    }
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      overflow: "visible"
    }
  })
}));

const AdminDashboard = ({
  activeLevelForMobile,
  open,
  setOpen,
  openSubscriptionModal,
  setOpenSubscriptionModal,
  openFreeTrialModal,
  setOpenFreeTrialModal,
  setActiveLevelForMobile,
  setIsShowingAllStats,
  setIsShowingLeaderBoard,
  setIsShowingCreateMeal,
  setIsShowingCreateRecipe,
  setIsShowingCreateCustomFood,
  setIsAddingMealItem,
  setEditMealToDiary,
  setEditRecipeToDiary,
  setEditCustomFoodToDiary,
  setEditOneOffToDiary,
  setIsViewingUserMeals,
  setIsViewingUserMealsMod
}) => {
  const [openCouponDirectly, setOpenCouponDirectly] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isInMobile = useIsMobile();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || isInMobile;

  const dispatch = useDispatch();
  const chatConversation = useSelector((s) => s.userMessaging?.conversation);
  const chatUnreadCount = useSelector((s) => s.userMessaging?.unreadCount || 0);
  const chatInitialized = useSelector((s) => s.userMessaging?.initialized);
  useFaviconBadge(chatUnreadCount);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const scrollableElement = document.getElementById("dashboard-main");
    scrollableElement.scrollTo(0, 0);
  }, []);

  // Fetch conversation once on mount so the Coach tab appears as soon as DM is enabled
  React.useEffect(() => {
    if (!chatInitialized) dispatch(fetchUserConversation());
  }, [dispatch, chatInitialized]);

  // ── Persistent background socket ──────────────────────────────────────────
  // Keeps listening for new messages on every page so the Coach badge stays
  // accurate even when the user is not on the /messages route.
  React.useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !chatConversation?._id) return;

    const socketUrl = new URL(process.env.REACT_APP_BACKEND_URL).origin;

    const socket = io(socketUrl, {
      auth: { token },
      transports: ["websocket"]
    });

    socket.on("connect", () => {
      // Re-fetch conversation on (re)connect to get the latest unread count
      dispatch(fetchUserConversation());
      socket.emit("join_conversation", {
        conversationId: chatConversation._id
      });
    });

    socket.on("new_message", (msg) => {
      if (msg.conversationId === chatConversation._id) {
        dispatch(appendMessage(msg));
        // Notify admin of delivery as soon as our socket receives the message,
        // regardless of whether isOnline() caught us as online at send time.
        if (msg.senderType === "admin" && msg._id) {
          socket.emit("mark_delivered", {
            conversationId: chatConversation._id,
            messageIds: [msg._id]
          });
        }
      }
    });

    socket.on(
      "message_status_update",
      ({ conversationId, messageIds, status }) => {
        if (conversationId === chatConversation._id) {
          dispatch(updateMessageStatus({ messageIds, status }));
        }
      }
    );

    return () => {
      socket.emit("leave_conversation", {
        conversationId: chatConversation._id
      });
      socket.disconnect();
    };
  }, [dispatch, chatConversation?._id]);
  const currentUser = useCurrentUser();
  const baseItems = [
    {
      text: "MOD",
      icon: <ModIcon />,
      activeIcon: <ActiveModIcon />,
      path: "/"
    },
    {
      text: "Diary",
      icon: <DiaryIcon />,
      activeIcon: <ActiveDiaryIcon />,
      path: "/diary"
    },
    {
      text: "Programs",
      icon: <ProgramIcon />,
      path: "/programs",
      activeIcon: <ActiveProgramIcon />
    },
    {
      text: isMobile ? "Meals" : "Meal Library",
      icon: <MealsIcon />,
      activeIcon: <ActiveMealsIcon />,
      path: "/meals"
    }
  ];

  const weeklyQAItem = {
    text: "Nutrition Q & A",
    icon: <LiveHelp sx={{ color: "#959ca7" }} />,
    activeIcon: <LiveHelp />,
    path: "/qa"
  };

  const profileItem = {
    text: "Profile",
    icon: <ProfileIcon />,
    activeIcon: <ActiveProfileIcon />,
    path: "/profile"
  };
  const { startDate, endDate } = currentUser?.masterclassData || {};
  const now = dayjs.utc();
  const isBetween =
    now.isAfter(dayjs.utc(startDate).subtract(1, "day")) &&
    now.isBefore(dayjs.utc(endDate).add(1, "day"));
  const isChatActive = location.pathname === "/messages";
  // Plain icons — sidebar wraps with dot badge in the ListItemIcon chain below
  const chatItem = {
    text: "Chat",
    icon: (
      <ChatBubbleOutline sx={{ color: isChatActive ? "#368AD0" : "#9CA3AF" }} />
    ),
    activeIcon: <Chat sx={{ color: "#368AD0" }} />,
    path: "/messages"
  };

  const menuItems = [
    ...baseItems,
    ...(chatConversation ? [chatItem] : []),
    ...((!currentUser?.masterclassData && currentUser?.userType !== "Coach") ||
    (currentUser?.masterclassData &&
      !isBetween &&
      currentUser?.userType !== "Coach")
      ? [weeklyQAItem]
      : []),
    profileItem
  ];

  const isPendingReadNotification = useSelector(selectPendingReadNotification);
  const { is_verified = false } = location.state || {
    is_verified: false
  };

  React.useEffect(() => {
    if (Boolean(is_verified)) {
      setOpenSubscriptionModal(false);
    } else if (
      !(
        currentUser?.isAdmin === 1 ||
        ["Coach", "Company"].includes(currentUser?.userType)
      ) &&
      !currentUser?.isFailedPayment &&
      !currentUser?.isSubscriptionActive &&
      currentUser?.isSubscriptionNeeded &&
      (currentUser?.isTrainingFreeTrialExpired ||
        currentUser?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [currentUser]);
  const freeTrialExpired = dayjs().diff(
    dayjs.utc(currentUser?.createdAt),
    "days"
  );

  const { startFreeTrial = false } = location.state || {
    startFreeTrial: false
  };

  React.useEffect(() => {
    if (currentUser?.isSuperAdmin) return;
    else if (
      !(
        currentUser?.isAdmin === 1 ||
        ["Coach", "Company"].includes(currentUser?.userType)
      ) &&
      startFreeTrial
    )
      setOpenFreeTrialModal(true);
  }, [startFreeTrial]);
  const lastDiaryVisitedToday = localStorage.getItem("diaryLastVisited")
    ? dayjs(localStorage.getItem("diaryLastVisited")).isSame(dayjs(), "day")
      ? true
      : false
    : null;
  return (
    <>
      {isMobile ? (
        <></>
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            ".MuiDrawer-paper": {
              display: "flex",
              justifyContent: "space-between",
              background: "#1C1C1C",
              color: "#9CA3AF"
            },
            display: { xs: "none", sm: "block" }
          }}
        >
          <Box>
            <DrawerHeader
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Box
                display="flex"
                sx={{
                  width: "85%",
                  height: "auto"
                }}
                gap="11px"
              >
                <Box
                  height={55}
                  component="img"
                  src="/images/threepillars.png"
                />
                <TPillars
                  style={{
                    width: "100%",
                    height: "auto"
                  }}
                />
              </Box>
              {/* Toggle Button for Drawer */}
              <IconButton
                onClick={() => setOpen(!open)}
                sx={{
                  position: "absolute",
                  padding: 0,
                  top: "50%",
                  right: -12, // Position based on drawer state
                  transform: "translateY(-50%)",
                  backgroundColor: "#1F2937",
                  color: "#9CA3AF",
                  "&:hover": {
                    backgroundColor: "#333333"
                  }
                }}
              >
                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List style={{ marginTop: "20px" }}>
              {menuItems.map(({ text, icon, path, activeIcon }) => (
                <ListItem key={text} sx={{ display: "block" }}>
                  <ListItemButton
                    key={Date.now()}
                    onClick={() => {
                      if (path === "/diary") {
                        setIsShowingAllStats(false);
                        setIsShowingLeaderBoard(false);
                        setIsShowingCreateMeal(false);
                        setIsShowingCreateRecipe(false);
                        setIsShowingCreateCustomFood(false);
                        setIsAddingMealItem(false);
                        setEditMealToDiary(false);
                        setEditRecipeToDiary(false);
                        setEditOneOffToDiary(false);
                        setEditCustomFoodToDiary(false);
                      } else if (path === "/meals") {
                        setIsViewingUserMeals(false);
                      } else if (path === "/") {
                        setIsViewingUserMealsMod(false);
                      }
                      navigate(path);
                    }}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      ...(location.pathname === path && {
                        background: "#1F2937",
                        borderRadius: "4px"
                      })
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                        position: "relative", // Make icon positioning relative
                        ...(location.pathname === path
                          ? ![""].includes(text)
                            ? {
                                "& svg path": {
                                  fill: "#368AD0"
                                }
                              }
                            : {
                                "& svg path": {
                                  stroke: "#368AD0",
                                  fill: "none"
                                }
                              }
                          : {})
                      }}
                    >
                      {text === "Programs" &&
                      !open &&
                      !currentUser?.masterclassData &&
                      currentUser?.activeLevel !== "level3" &&
                      (!currentUser?.isSubscriptionNeeded ||
                        !currentUser?.isTrainingFreeTrialExpired) &&
                      currentUser?.pendingTrainingDays > 0 ? (
                        <Badge
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#FFF",
                              backgroundColor: "#F6541C",
                              position: "absolute", // Set badge position as absolute
                              top: open ? 0 : 2, // Adjust based on drawer state
                              left: open ? "60px" : "10px" // Adjust badge positioning for closed state
                            }
                          }}
                          badgeContent={
                            currentUser?.isSubscriptionActive
                              ? 0
                              : !currentUser?.isSubscriptionNeeded
                                ? currentUser?.pendingTrainingDays
                                : !currentUser?.isTrainingFreeTrialExpired
                                  ? Math.min(
                                      5,
                                      currentUser?.pendingTrainingDays
                                    )
                                  : currentUser?.pendingTrainingDays
                          }
                        >
                          {location.pathname === path ? activeIcon : icon}
                        </Badge>
                      ) : text === "Diary" &&
                        !window.location.pathname.includes("diary") &&
                        !open &&
                        (!currentUser?.isDiarySubscriptionNeeded ||
                          !currentUser?.isDiaryFreeTrialExpired) &&
                        !lastDiaryVisitedToday ? (
                        <Badge
                          variant="dot"
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#FFF",
                              backgroundColor: "#F6541C",
                              left: "12px",
                              minWidth: "12px",
                              height: "12px",
                              borderRadius: "50%"
                            }
                          }}
                        >
                          {location.pathname === path ? activeIcon : icon}
                        </Badge>
                      ) : text === "Profile" &&
                        !open &&
                        isPendingReadNotification ? (
                        <Badge
                          variant="dot"
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#FFF",
                              backgroundColor: "#F6541C",
                              left: "12px",
                              minWidth: "12px",
                              height: "12px",
                              borderRadius: "50%"
                            }
                          }}
                        >
                          {location.pathname === path ? activeIcon : icon}
                        </Badge>
                      ) : text === "Nutrition Q & A" &&
                        !open &&
                        currentUser?.checkQALinkReminder ? (
                        <Badge
                          variant="dot"
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#FFF",
                              backgroundColor: "#F6541C",
                              left: "12px",
                              minWidth: "12px",
                              height: "12px",
                              borderRadius: "50%"
                            }
                          }}
                        >
                          {location.pathname === path ? activeIcon : icon}
                        </Badge>
                      ) : text === "Chat" && !open && chatUnreadCount > 0 ? (
                        <Badge
                          variant="dot"
                          sx={{
                            "& .MuiBadge-badge": {
                              color: "#FFF",
                              backgroundColor: "#F6541C",
                              left: "12px",
                              minWidth: "12px",
                              height: "12px",
                              borderRadius: "50%"
                            }
                          }}
                        >
                          {location.pathname === path ? activeIcon : icon}
                        </Badge>
                      ) : location.pathname === path ? (
                        activeIcon
                      ) : (
                        icon
                      )}
                    </ListItemIcon>
                    {text === "Programs" &&
                    open &&
                    !currentUser?.masterclassData &&
                    currentUser?.activeLevel !== "level3" &&
                    (!currentUser?.isSubscriptionNeeded ||
                      !currentUser?.isTrainingFreeTrialExpired) &&
                    currentUser?.pendingTrainingDays > 0 ? (
                      <Badge
                        sx={{
                          "& .MuiBadge-badge": {
                            color: "#FFF",
                            backgroundColor: "#F6541C",
                            left: "60px"
                          }
                        }}
                        badgeContent={
                          currentUser?.isSubscriptionActive
                            ? 0
                            : !currentUser?.isSubscriptionNeeded
                              ? currentUser?.pendingTrainingDays
                              : !currentUser?.isTrainingFreeTrialExpired
                                ? Math.min(5, currentUser?.pendingTrainingDays)
                                : currentUser?.pendingTrainingDays
                        }
                      >
                        <ListItemText
                          primary={text}
                          sx={{
                            opacity: open ? 1 : 0,
                            ...(location.pathname === path && {
                              color: "#368AD0"
                            })
                          }}
                        />
                      </Badge>
                    ) : text === "Diary" &&
                      !window.location.pathname.includes("diary") &&
                      open &&
                      (!currentUser?.isDiarySubscriptionNeeded ||
                        !currentUser?.isDiaryFreeTrialExpired) &&
                      !lastDiaryVisitedToday ? (
                      <Badge
                        variant="dot"
                        sx={{
                          "& .MuiBadge-badge": {
                            color: "#FFF",
                            backgroundColor: "#F6541C",
                            left: "30px",
                            minWidth: "15px",
                            height: "15px",
                            borderRadius: "50%"
                          }
                        }}
                      >
                        <ListItemText
                          primary={text}
                          sx={{
                            opacity: open ? 1 : 0,
                            ...(location.pathname === path && {
                              color: "#368AD0"
                            })
                          }}
                        />
                      </Badge>
                    ) : text === "Profile" &&
                      open &&
                      isPendingReadNotification ? (
                      <Badge
                        variant="dot"
                        sx={{
                          "& .MuiBadge-badge": {
                            color: "#FFF",
                            backgroundColor: "#F6541C",
                            left: "40px",
                            minWidth: "12px",
                            height: "12px",
                            borderRadius: "50%"
                          }
                        }}
                      >
                        <ListItemText
                          primary={text}
                          sx={{
                            opacity: open ? 1 : 0,
                            ...(location.pathname === path && {
                              color: "#368AD0"
                            })
                          }}
                        />
                      </Badge>
                    ) : text === "Nutrition Q & A" &&
                      currentUser?.checkQALinkReminder &&
                      open ? (
                      <Badge
                        variant="dot"
                        sx={{
                          "& .MuiBadge-badge": {
                            color: "#FFF",
                            backgroundColor: "#F6541C",
                            right: "-8px",
                            minWidth: "12px",
                            height: "12px",
                            borderRadius: "50%"
                          }
                        }}
                      >
                        <ListItemText
                          primary={text}
                          sx={{
                            opacity: open ? 1 : 0,
                            ...(location.pathname === path && {
                              color: "#368AD0"
                            })
                          }}
                        />
                      </Badge>
                    ) : text === "Chat" && open && chatUnreadCount > 0 ? (
                      <Badge
                        badgeContent={chatUnreadCount}
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            bgcolor: "#F6541C",
                            color: "#fff",
                            fontSize: 11,
                            right: "-8px",
                            fontWeight: 700
                          }
                        }}
                      >
                        <ListItemText
                          primary={text}
                          sx={{
                            opacity: open ? 1 : 0,
                            ...(location.pathname === path && {
                              color: "#368AD0"
                            })
                          }}
                        />
                      </Badge>
                    ) : (
                      <ListItemText
                        primary={text}
                        sx={{
                          opacity: open ? 1 : 0,
                          ...(location.pathname === path && {
                            color: "#368AD0"
                          })
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
          {open && !currentUser?.isSubscriptionActive && (
            <Box
              margin="0 16px 40px 16px"
              alignItems={"center"}
              justifyContent={"center"}
              display="flex"
              flexDirection="column"
              sx={{
                // background: "#333333",
                padding: "20px",
                color: "#9CA3AF",
                borderRadius: "4px",
                display: { xs: "none", sm: "flex" }
              }}
            >
              {/* <PremiumIcon />
            <Typography mt={2} fontSize="16px" fontWeight={600} color="#FCFCFC">
              3 Pillar full Access
            </Typography>
            <Typography fontSize="12px" fontWeight={400} color="#FCFCFC">
              Get all benefits
            </Typography> */}
              <Button
                fullWidth
                variant="outlined"
                style={{
                  textTransform: "none",
                  background: "#368AD0",
                  color: "#FCFCFC",
                  border: "none",
                  marginTop: "10px"
                }}
                onClick={() => setOpenFreeTrialModal(true)}
              >
                Free Trial (
                {freeTrialExpired >= 5
                  ? "Expired"
                  : 5 - freeTrialExpired === 1
                    ? "Last Day"
                    : `${5 - freeTrialExpired} days left`}
                )
              </Button>
              <Button
                fullWidth
                variant="outlined"
                style={{
                  textTransform: "none",
                  color: "#368AD0",
                  borderColor: "#368AD0",
                  marginTop: "10px"
                }}
                onClick={() => setOpenSubscriptionModal(true)}
              >
                View Plans
              </Button>
            </Box>
          )}
          {open && currentUser?.activeSubscriptionData?.cancelAtPeriodEnd && (
            <Box
              margin="0 16px 40px 16px"
              alignItems={"center"}
              justifyContent={"center"}
              display="flex"
              flexDirection="column"
              sx={{
                padding: "20px",
                color: "#9CA3AF",
                borderRadius: "4px",
                display: { xs: "none", sm: "flex" }
              }}
            >
              <Button
                fullWidth
                variant="contained"
                style={{
                  textTransform: "none",
                  color: "#fff",
                  backgroundColor: "#D32F2F",
                  marginTop: "10px"
                }}
                onClick={() => navigate("/manage-billing")}
              >
                Subscription Cancelled
              </Button>
            </Box>
          )}
          {/* {open &&
            currentUser?.userType === "Coach" &&
            !currentUser?.isAdmin && (
              <Box
                margin="0 16px 40px 16px"
                alignItems={"center"}
                justifyContent={"center"}
                display="flex"
                flexDirection="column"
                sx={{
                  // background: "#333333",
                  padding: "20px",
                  color: "#9CA3AF",
                  borderRadius: "4px",
                  display: { xs: "none", sm: "flex" }
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  style={{
                    textTransform: "none",
                    color: "#368AD0",
                    borderColor: "#368AD0",
                    marginTop: "10px"
                  }}
                  onClick={() => {
                    setOpenCouponDirectly(true);
                  }}
                >
                  Apply Coupon
                </Button>
              </Box>
            )} */}
        </Drawer>
      )}
      <SubscriptionModal
        open={openSubscriptionModal}
        onClose={() => setOpenSubscriptionModal(false)}
        isMobile={isMobile}
        openCouponDirectly={openCouponDirectly}
      />
      {openCouponDirectly && (
        <CouponModal
          open={openCouponDirectly}
          onClose={() => {
            setOpenCouponDirectly(false);
          }}
        />
      )}
      <FreeTrialModal
        open={openFreeTrialModal}
        onClose={() => setOpenFreeTrialModal(false)}
        isMobile={isMobile}
        disabledFreeTrial={freeTrialExpired > 5}
        openSubscriptionModal={() => setOpenSubscriptionModal(true)}
      />
      {isMobile && (
        <BottomNavigation
          showLabels
          sx={{
            height: "72px",
            display: isInMobile ? "flex" : { xs: "flex", sm: "none" },
            position: "fixed",
            bottom: 0,
            width: "100%",
            zIndex: 1000000,
            backgroundColor: "#2A2A2A",
            borderTop: "1px solid #3D3D3D",
            "& .MuiBottomNavigationAction-root": {
              minWidth: 0,
              flex: 1,
              padding: "10px 4px 8px",
              gap: "4px",
              color: "#9CA3AF",
              transition: "color 0.15s"
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "10px !important",
              lineHeight: 1.2,
              marginTop: "2px"
            },
            "& .Mui-selected": {
              color: "#368AD0 !important"
            },
            "& svg": { width: "24px", height: "24px", fontSize: "24px" }
          }}
          value={location.pathname}
          onChange={(event, newValue) => {
            if (newValue === "/diary") {
              setIsShowingAllStats(false);
              setIsShowingLeaderBoard(false);
              setIsShowingCreateMeal(false);
              setIsShowingCreateRecipe(false);
              setIsShowingCreateCustomFood(false);
              setIsAddingMealItem(false);
              setEditMealToDiary(false);
              setEditOneOffToDiary(false);
              setEditRecipeToDiary(false);
              setEditCustomFoodToDiary(false);
            } else if (newValue === "/meals") {
              setIsViewingUserMeals(false);
            } else if (newValue === "/") {
              setIsViewingUserMealsMod(false);
            }
            navigate(newValue);
          }}
        >
          {menuItems.map(({ text, icon, activeIcon, path }) => (
            <BottomNavigationAction
              key={text}
              label={text === "Nutrition Q & A" ? "Q & A" : text}
              icon={
                text === "Programs" &&
                (!currentUser?.isSubscriptionNeeded ||
                  !currentUser?.isTrainingFreeTrialExpired) &&
                currentUser?.pendingTrainingDays ? (
                  <Badge
                    badgeContent={
                      currentUser?.isSubscriptionActive
                        ? 0
                        : currentUser?.pendingTrainingDays
                    }
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        right: -10, // Adjusts badge position
                        top: -5 // Adjusts badge position
                      }
                    }}
                  >
                    {location.pathname === path ? activeIcon : icon}
                  </Badge>
                ) : text === "Diary" &&
                  !localStorage.getItem("diaryLastVisited") ? (
                  <Badge
                    variant="dot"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "15px",
                        minWidth: "12px",
                        height: "12px",
                        borderRadius: "50%"
                      }
                    }}
                  >
                    {location.pathname === path ? activeIcon : icon}
                  </Badge>
                ) : text === "Profile" && isPendingReadNotification ? (
                  <Badge
                    variant="dot"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "15px",
                        minWidth: "12px",
                        height: "12px",
                        borderRadius: "50%"
                      }
                    }}
                  >
                    {location.pathname === path ? activeIcon : icon}
                  </Badge>
                ) : text === "Nutrition Q & A" &&
                  currentUser?.checkQALinkReminder ? (
                  <Badge
                    variant="dot"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#FFF",
                        backgroundColor: "#F6541C",
                        left: "15px",
                        minWidth: "12px",
                        height: "12px",
                        borderRadius: "50%"
                      }
                    }}
                  >
                    {location.pathname === path ? activeIcon : icon}
                  </Badge>
                ) : text === "Chat" && chatUnreadCount > 0 ? (
                  <Badge
                    badgeContent={chatUnreadCount}
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        bgcolor: "#F6541C",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700
                      }
                    }}
                  >
                    {location.pathname === path ? activeIcon : icon}
                  </Badge>
                ) : location.pathname === path ? (
                  activeIcon
                ) : (
                  icon
                )
              }
              value={path}
              sx={{
                "&.MuiButtonBase-root": {
                  ...(location.pathname === path
                    ? text !== ""
                      ? { "& svg path": { fill: "#368AD0" } }
                      : { "& svg path": { stroke: "#368AD0", fill: "none" } }
                    : {})
                }
              }}
            />
          ))}
        </BottomNavigation>
      )}
    </>
  );
};

export default AdminDashboard;
